import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectDeliverables() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', type: 'document', due_date: '', acceptance_criteria: '', approval_required: false, client_approval_required: false });
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [statusAction, setStatusAction] = useState(null);

  const load = useCallback(() => {
    if (!companyId || !projectId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/projects/${projectId}/deliverables`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setDeliverables(Array.isArray(d) ? d : (d && Array.isArray(d.data) ? d.data : [])))
      .catch(() => setDeliverables([])).finally(() => setLoading(false));
  }, [companyId, projectId, API]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = (id) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/deliverables/${id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => { setDetail(d); setShowDetail(true); }).catch(() => {});
  };

  const handleSave = () => {
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${API}/companies/${companyId}/projects/${projectId}/deliverables/${editId}` : `${API}/companies/${companyId}/projects/${projectId}/deliverables`;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowModal(false); setEditId(null); resetForm(); load(); })
      .catch(() => window.alert('Failed to save deliverable'));
  };

  const resetForm = () => setForm({ name: '', description: '', type: 'document', due_date: '', acceptance_criteria: '', approval_required: false, client_approval_required: false });

  const changeStatus = (id, status, extra = {}) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/deliverables/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status, ...extra })
    }).then(r => r.ok ? r.json() : null).then(() => { load(); if (detail?.id === id) loadDetail(id); setStatusAction(null); }).catch(() => {});
  };

  const addComment = (id) => {
    if (!newComment.trim()) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/deliverables/${id}/comments`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ comment: newComment })
    }).then(r => r.ok ? r.json() : null).then(() => { setNewComment(''); loadDetail(id); }).catch(() => {});
  };

  const openEdit = (d) => {
    setEditId(d.id);
    setForm({
      name: d.name || '', description: d.description || '', type: d.type || 'document',
      due_date: d.due_date || '', acceptance_criteria: d.acceptance_criteria || '',
      approval_required: !!d.approval_required, client_approval_required: !!d.client_approval_required,
    });
    setShowModal(true);
  };

  const statusColor = { draft: '#94a3b8', in_progress: '#3b82f6', submitted: '#8b5cf6', in_review: '#f59e0b', approved: '#22c55e', rejected: '#ef4444', delivered: '#10b981', cancelled: '#6b7280' };
  const typeIcon = { document: '📄', report: '📊', presentation: '📽️', code: '💻', design: '🎨', other: '📦' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <button onClick={() => navigate(`/company/projects/${projectId}`)} style={linkBtn}>← Back to Project</button>
          <h2 style={{ margin: '4px 0 0', fontSize: '1.4rem', fontWeight: 700 }}>📦 Deliverables</h2>
        </div>
        <button onClick={() => { setEditId(null); resetForm(); setShowModal(true); }} style={primaryBtn}>+ New Deliverable</button>
      </div>

      {initialLoading ? <p>Loading...</p> : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {deliverables.map(d => {
            const isOverdue = d.due_date && new Date(d.due_date) < new Date() && !['approved','delivered','cancelled'].includes(d.status);
            return (
              <div key={d.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => loadDetail(d.id)}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span>{typeIcon[d.type] || '📦'}</span>
                    <span style={{ fontWeight: 600 }}>{d.name}</span>
                    <span style={{ ...badge, backgroundColor: statusColor[d.status] || '#6b7280' }}>{(d.status || '').replace(/_/g,' ')}</span>
                    {d.revision_number > 1 && <span style={{ fontSize: '0.65rem', color: '#8b5cf6', fontWeight: 600 }}>Rev {d.revision_number}</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '16px' }}>
                    <span>{d.reference}</span>
                    <span style={{ color: isOverdue ? '#ef4444' : '#64748b' }}>Due: {d.due_date || 'Not set'}</span>
                    <span>Files: {d.files_count || 0}</span>
                    {d.approval_required && <span style={{ color: '#f59e0b' }}>Approval Required</span>}
                    {d.client_approval_required && <span style={{ color: '#8b5cf6' }}>Client Approval</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={(e) => { e.stopPropagation(); openEdit(d); }} style={smallBtn}>Edit</button>
                  {d.status === 'in_progress' && <button onClick={(e) => { e.stopPropagation(); changeStatus(d.id, 'submitted'); }} style={{ ...smallBtn, color: '#8b5cf6' }}>Submit</button>}
                  {d.status === 'submitted' && <button onClick={(e) => { e.stopPropagation(); changeStatus(d.id, 'approved'); }} style={{ ...smallBtn, color: '#22c55e' }}>Approve</button>}
                  {d.status === 'approved' && <button onClick={(e) => { e.stopPropagation(); changeStatus(d.id, 'delivered'); }} style={{ ...smallBtn, color: '#10b981' }}>Mark Delivered</button>}
                </div>
              </div>
            );
          })}
          {deliverables.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center' }}>No deliverables yet</p>}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{editId ? 'Edit Deliverable' : 'New Deliverable'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input placeholder="Deliverable Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
              <div>
                <label style={labelStyle}>Type</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}>
                  <option value="document">Document</option>
                  <option value="report">Report</option>
                  <option value="presentation">Presentation</option>
                  <option value="code">Code</option>
                  <option value="design">Design</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Due Date</label>
                <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} style={inputStyle} />
              </div>
              <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, minHeight: '50px' }} />
              <textarea placeholder="Acceptance Criteria" value={form.acceptance_criteria} onChange={e => setForm({ ...form, acceptance_criteria: e.target.value })} style={{ ...inputStyle, minHeight: '50px' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={form.approval_required} onChange={e => setForm({ ...form, approval_required: e.target.checked })} /> Internal Approval Required
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={form.client_approval_required} onChange={e => setForm({ ...form, client_approval_required: e.target.checked })} /> Client Approval Required
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              <button onClick={handleSave} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Panel */}
      {showDetail && detail && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '650px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px' }}>{typeIcon[detail.type] || '📦'} {detail.name}</h3>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{detail.reference}</span>
              </div>
              <button onClick={() => setShowDetail(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <span style={{ ...badge, backgroundColor: statusColor[detail.status] || '#6b7280' }}>{(detail.status || '').replace(/_/g,' ')}</span>
              {detail.revision_number > 1 && <span style={{ ...badge, backgroundColor: '#8b5cf6' }}>Rev {detail.revision_number}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px', fontSize: '0.82rem' }}>
              <div><strong>Type:</strong> {detail.type}</div>
              <div><strong>Due:</strong> {detail.due_date || '-'}</div>
              <div><strong>Submitted:</strong> {detail.submitted_at || '-'}</div>
              <div><strong>Approved:</strong> {detail.approved_at || '-'}</div>
              <div><strong>Delivered:</strong> {detail.delivered_at || '-'}</div>
              <div><strong>Revision:</strong> {detail.revision_number || 1}</div>
            </div>

            {detail.description && <div style={{ fontSize: '0.82rem', marginBottom: '12px' }}><strong>Description:</strong> {detail.description}</div>}
            {detail.acceptance_criteria && <div style={{ fontSize: '0.82rem', marginBottom: '12px' }}><strong>Acceptance Criteria:</strong> {detail.acceptance_criteria}</div>}

            {/* Status Actions */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {detail.status === 'draft' && <button onClick={() => changeStatus(detail.id, 'in_progress')} style={actionBtn}>Start Work</button>}
              {detail.status === 'in_progress' && <button onClick={() => changeStatus(detail.id, 'submitted')} style={{ ...actionBtn, backgroundColor: '#8b5cf6' }}>Submit</button>}
              {detail.status === 'submitted' && <button onClick={() => changeStatus(detail.id, 'in_review')} style={{ ...actionBtn, backgroundColor: '#f59e0b' }}>Start Review</button>}
              {detail.status === 'in_review' && <button onClick={() => changeStatus(detail.id, 'approved')} style={{ ...actionBtn, backgroundColor: '#22c55e' }}>Approve</button>}
              {detail.status === 'in_review' && <button onClick={() => changeStatus(detail.id, 'rejected')} style={{ ...actionBtn, backgroundColor: '#ef4444' }}>Reject</button>}
              {detail.status === 'approved' && <button onClick={() => changeStatus(detail.id, 'delivered')} style={{ ...actionBtn, backgroundColor: '#10b981' }}>Mark Delivered</button>}
            </div>

            {/* Client Approval */}
            {detail.client_approval_required && (
              <div style={{ ...cardInner, marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Client Approval</h4>
                <div style={{ fontSize: '0.82rem' }}>
                  <div>Status: <strong>{detail.client_approved ? '✅ Approved' : '⏳ Pending'}</strong></div>
                  {detail.client_approved_at && <div>Approved: {detail.client_approved_at}</div>}
                  {detail.client_approved_by && <div>By: {detail.client_approved_by}</div>}
                </div>
              </div>
            )}

            {/* Files */}
            {Array.isArray(detail.files) && detail.files.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Files ({detail.files.length})</h4>
                {detail.files.map(f => (
                  <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                    <span>{f.original_name || f.file_name}</span>
                    <span style={{ color: '#94a3b8' }}>v{f.version} — {f.uploaded_by?.name || '-'}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Comments */}
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Comments</h4>
              {Array.isArray(detail.comments) && detail.comments.map(c => (
                <div key={c.id} style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                  <strong>{c.user?.name || 'Unknown'}</strong> <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{new Date(c.created_at).toLocaleString()}</span>
                  <div>{c.comment}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                <input placeholder="Add comment..." value={newComment} onChange={e => setNewComment(e.target.value)} style={{ ...inputStyle, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addComment(detail.id)} />
                <button onClick={() => addComment(detail.id)} style={smallBtn}>Post</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const cardInner = { backgroundColor: '#f8fafc', borderRadius: '6px', padding: '12px', border: '1px solid #e2e8f0' };
const badge = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' };
const primaryBtn = { padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const cancelBtn = { padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' };
const smallBtn = { padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' };
const actionBtn = { padding: '6px 12px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 };
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85rem', padding: 0 };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' };
